import { Typography, makeStyles } from '@material-ui/core'
import Link from 'next/link'
import React from 'react'
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'

import { fundKey } from '../../lib/grantsDB'
import { fundLabels } from './PayoutsByFund'

const useStyles = makeStyles((theme) => ({
  chartWrap: {
    marginBottom: theme.spacing(10),
  },
  chartTitle: {
    textAlign: 'center',
  },
  chartRoot: {
    marginBottom: theme.spacing(6),
    width: '100%',
    height: 260,
    fontFamily: '"Lato", sans-serif',
  },
}))

export type InstitutionalFundingData = {
  mostRecentYear: number
  fundingByFund: Record<fundKey, PieChartData[]>
}

type PieChartData = {
  name: string // x-axis label
  value: number
}

export const InstitutionalFunding = ({
  allData,
}: {
  allData: InstitutionalFundingData
}) => {
  const classes = useStyles()
  return (
    <>
      <Typography variant="body1" gutterBottom>
        EA Funds is financially supported by both{' '}
        <Link href="/stats/donations">individual funders</Link> and
        institutional funders, such as{' '}
        <Link href="https://www.openphilanthropy.org/">Open Philanthropy</Link>.
      </Typography>

      <Typography variant="body1" gutterBottom>
        Institutional donors may opt to collaborate with us for a variety of
        reasons, such as leveraging our ability to identify funding
        opportunities, a desire to diversify the funding landscape in cause
        areas we operate in, valuing the specialized knowledge of our fund
        managers, or addressing limitations in their grantmaking capacity.
      </Typography>

      <Typography variant="body1" gutterBottom>
        Our largest institutional donor is Open Philanthropy.
        {/* You can read more about our relationship with them <Link to='/our-relationship-to-open-philanthropy'>here</Link>. */}
      </Typography>

      {Object.keys(allData.fundingByFund).map((key) => (
        <div key={key} className={classes.chartWrap}>
          <Typography variant="h4" className={classes.chartTitle}>
            {fundLabels[key]} Fund
          </Typography>
          <Typography variant="h6" gutterBottom className={classes.chartTitle}>
            {allData.mostRecentYear} Funding Sources
          </Typography>

          <FundingChart data={allData.fundingByFund[key as fundKey]} />
        </div>
      ))}
    </>
  )
}

const COLORS: Record<string, string> = {
  'Open Philanthropy': '#88bf4d',
  Institutional: '#a989c5',
  'Other Institutional': '#a989c5',
  'Direct Donation': '#f2a86f',
}

const formatDollars = (val: number) =>
  Number(val as number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
  })

export const FundingChart = ({ data }: { data: PieChartData[] }) => {
  const classes = useStyles()

  return (
    <div className={classes.chartRoot}>
      <ResponsiveContainer>
        <PieChart height={150}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={80}
            labelLine={false}
            label={({ value }) => formatDollars(value)}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
            ))}
          </Pie>
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
